import React, { useState } from 'react';
import { Input, Typography, Row, Col, Space, message } from 'antd';
import { ThunderboltOutlined, ClearOutlined } from '@ant-design/icons';
import { Sparkles } from 'lucide-react';
import { MenuSections } from '../types/menu-sections';
import { useBanners } from '../context/banners';
import { useModal } from '../context/modal';
import { useCreatePromptBanner } from '../hooks/banners/creation/api/use-create-prompt-banner';
import { useInitializeBanner } from '../hooks/banners/creation/use-initialize-banner';
import { useClientId } from '../hooks/use-client-id';
import { useResponsiveSizes } from '../hooks/use-responsive-sizes';
import { palette } from '@doer/frontend-components/theme';
import { tokens } from '../theme/tokens';
import { TemplateCard } from '../components/ui/template-card';
import { ActionButton } from '@doer/frontend-components/buttons';
import { Spinner } from '@doer/frontend-components/spinner';
import { getCreativityOptions } from '../clients/registry';
import { ResponsiveSelect } from '../components/ui/inputs/responsive-select';
import { getClientOptions } from '../clients/client-options';
import { PageTitle } from '../components/ui/layout/page-title';
import { PageContainer } from '../components/ui/layout/page-container';
import { AppCard } from '../components/ui/layout/app-card';
import { useValidateMedia } from '../hooks/banners/use-validate-media';
import { isAltEnvironment } from '../utils';
import { Pill } from '@doer/frontend-components/pill';

const { Title, Text } = Typography;
const { TextArea } = Input;

const TEST_CLIENT_ID = 'TEST';

interface CreateBannerPageProps {
  onNavigate?: (section: MenuSections) => void;
}

function normalizeUrl(raw: string): string {
  return raw
    .trim()
    .replace(/^["'(<]+/, '')
    .replace(/["')>,.;:!]+$/, '');
}

function extractUrlsFromText(text: string): string[] {
  if (!text) return [];

  const regex = /(https?:\/\/[^\s]+)/gi;
  const matches = text.match(regex) || [];

  const out: string[] = [];
  const seen: Record<string, true> = {};

  for (let i = 0; i < matches.length; i++) {
    const u = normalizeUrl(matches[i]);
    if (!seen[u]) {
      seen[u] = true;
      out.push(u);
    }
  }

  return out;
}

export const CreateBannerPage: React.FC<CreateBannerPageProps> = ({ onNavigate }) => {
  const { fontSize, screens, spacing } = useResponsiveSizes();
  const { createPromptBanner } = useCreatePromptBanner();
  const { initializeBanner } = useInitializeBanner();
  const { refreshAllBanners } = useBanners();
  const { openAsset } = useModal();
  const { validateMediaUrl } = useValidateMedia();

  const [promptError, setPromptError] = useState<string | null>(null);
  const [isPromptChecking, setIsPromptChecking] = useState(false);

  const [prompt, setPrompt] = useState('');
  const [selectedClient, setSelectedClient] = useState<string | undefined>();
  const [isGenerating, setIsGenerating] = useState(false);

  const clientId = useClientId();
  const isTestUser = clientId === TEST_CLIENT_ID;
  const availableTemplates = getCreativityOptions(clientId);
  const isAlt = isAltEnvironment();

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    const ok = await validatePromptUrls();
    if (!ok) {
      message.error('La URL incluida en el prompt no es válida');
      return;
    }

    if (isTestUser && !selectedClient) {
      message.error('Por favor selecciona un cliente');
      return;
    }

    setIsGenerating(true);

    try {
      const bannerId = await initializeBanner();

      const result = await createPromptBanner(bannerId, prompt, isTestUser ? selectedClient : undefined);

      if (result) {
        message.success('Procesando tu solicitud ...');
        await refreshAllBanners();

        openAsset(result.banner.bannerId);

        if (onNavigate) {
          onNavigate('dashboard');
        }
      } else {
        message.error('No se pudo procesar tu solicitud. Intentá nuevamente.');
      }
    } catch (error) {
      console.error('Error generating asset:', error);
      message.error('No se pudo procesar tu solicitud. Intentá nuevamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  const validatePromptUrls = async (): Promise<boolean> => {
    const urls = extractUrlsFromText(prompt);

    if (!urls.length) {
      setPromptError(null);
      return true;
    }

    setIsPromptChecking(true);

    try {
      for (let i = 0; i < urls.length; i++) {
        const res = await validateMediaUrl(urls[i]);

        if (!res?.ok) {
          setPromptError('El prompt contiene una URL de imagen o video inválida');
          return false;
        }
      }

      setPromptError(null);
      return true;
    } catch {
      setPromptError('No se pudo validar la URL del prompt');
      return false;
    } finally {
      setIsPromptChecking(false);
    }
  };

  const handleClear = () => {
    setPrompt('');
    setSelectedClient(undefined);
  };

  const handlePredefinedPrompt = (selectedPrompt: string) => {
    setPrompt(selectedPrompt);
  };

  return (
    <PageContainer>
      <PageTitle style={{ marginBottom: spacing.md }}>Plantillas</PageTitle>
      <AppCard>
        <Title
          style={{
            marginBottom: 8,
            marginTop: 0,
            display: 'flex',
            gap: spacing.sm,
            alignItems: 'center',
            fontSize: screens.xs ? fontSize.xl : fontSize.xxl,
            fontWeight: 500,
            color: tokens.colors.primary.neutral[1010]
          }}
        >
          <Sparkles size={25} color={palette.primitives.purple.purple550} />
          Crear asset
        </Title>

        <Text
          style={{
            marginBottom: spacing.md,
            display: 'flex',
            fontSize: fontSize.xlg,
            color: tokens.colors.primary.neutral[550],
            fontWeight: 400,
            lineHeight: 1.5
          }}
        >
          Describí tu idea y la IA generará opciones para vos
        </Text>

        {/* Client Dropdown */}
        {isTestUser && (
          <div style={{ marginBottom: 16, display: 'flex', gap: 15, alignItems: 'center', fontSize: fontSize.xlg }}>
            <Text style={{ display: 'block', fontSize: fontSize.xlg, lineHeight: 1.5 }}>Cliente del template:</Text>
            <ResponsiveSelect
              placeholder='Seleccionar cliente'
              value={selectedClient}
              onChange={setSelectedClient}
              options={getClientOptions()}
            />
          </div>
        )}

        <Row gutter={16}>
          <Col flex='auto'>
            <TextArea
              placeholder='Ej: Necesito un video para una campaña de Black Friday con colores oscuros y llamativo...'
              value={prompt}
              onChange={e => {
                setPrompt(e.target.value);
                if (promptError) setPromptError(null);
              }}
              onBlur={validatePromptUrls}
              rows={4}
              disabled={isGenerating || isAlt}
              style={{ fontSize: fontSize.xlg }}
              status={promptError ? 'error' : undefined}
            />
            {promptError && <Text style={{ color: 'red', marginTop: 6 }}>{promptError}</Text>}
          </Col>
          <Col>
            <div
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-evenly'
              }}
            >
              <ActionButton
                variant='primary'
                size='L'
                fullWidth
                onClick={handleGenerate}
                disabled={!prompt.trim() || !!promptError || isPromptChecking || isAlt || isGenerating}
              >
                {isGenerating ? <Spinner size={16} /> : <ThunderboltOutlined />}
                {isGenerating ? 'Creando...' : 'Generar'}
              </ActionButton>

              <ActionButton
                variant='outline'
                size='L'
                fullWidth
                onClick={handleClear}
                disabled={!prompt.trim() || isAlt}
              >
                <ClearOutlined />
                Limpiar
              </ActionButton>
            </div>
          </Col>
        </Row>

        <div style={{ marginTop: 16 }}>
          <Text style={{ marginBottom: 8, display: 'flex', fontSize: fontSize.xlg }}>Ejemplos rápidos:</Text>
          <Space wrap style={{ display: 'flex' }}>
            {availableTemplates
              .filter(t => !t.disabled)
              .map(template => {
                if (template.mode !== 'legacy' || template.hidePromptTemplate || !template.promptTemplate) return null;
                const prompt = template.promptTemplate;
                return (
                  <Pill
                    key={template.id}
                    onClick={() => handlePredefinedPrompt(prompt)}
                    disabled={isAlt}
                    variant='outline'
                    size='XS'
                  >
                    {template.title}
                  </Pill>
                );
              })}
          </Space>
        </div>
      </AppCard>

      <div style={{ margin: '32px 0' }}>
        <Text
          style={{
            fontSize: fontSize.xl,
            fontWeight: 500,
            marginBottom: 24,
            display: 'flex',
            justifyContent: 'center',
            color: tokens.colors.primary.neutral[1010]
          }}
        >
          Elegí un template prediseñado
        </Text>

        {/* Grid con dense packing */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: screens.xs ? 12 : screens.sm ? 30 : 40,
            width: '100%',
            justifyContent: 'flex-start'
          }}
        >
          {availableTemplates.map(template => {
            const previewImage = template.thumbnail.optimizedUrl ?? template.thumbnail.url;
            const previewVideo = template.thumbnail.hoverVideo;
            const hasCanvasBinding = template.mode === 'wysiwyg';
            return (
              <div key={template.id}>
                {template.disabled && previewVideo ? (
                  <div>
                    <TemplateCard
                      title={template.title}
                      onClick={() => {}}
                      disabled={template.disabled}
                      image={previewImage}
                      video={previewVideo}
                      aspectRatio={template.thumbnail.aspectRatio}
                    />
                  </div>
                ) : (
                  <TemplateCard
                    title={template.title}
                    onClick={() => {
                      const encodedId = encodeURIComponent(template.id);
                      const section = hasCanvasBinding
                        ? (`creativity-editor/${encodedId}` as const)
                        : (`creativity-form/${encodedId}` as const);
                      onNavigate?.(section);
                    }}
                    disabled={template.disabled}
                    image={previewImage}
                    aspectRatio={template.thumbnail.aspectRatio}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </PageContainer>
  );
};
